/**
 * Box-score column definitions — single source of truth (Bible § 14).
 *
 * The box-score column list is derived from
 * league_settings.scoring_categories + a fixed context prepend, NOT a
 * hardcoded list. Context stats are the basic per-game numbers a baseball
 * reader always expects (AB for hitters; IP/H/ER for pitchers); everything
 * else comes from the league's scored categories.
 *
 * Extracted 2026-05-26 (Chunk 3.16 Bug 2) from MobileTeamBoxScore so the
 * full box-score PAGE and the League-Home box-score TILE consume ONE
 * definition — the tile previously had hardcoded headers that omitted WHIP.
 */

import React from 'react';
import { dynastyTheme } from '../../../services/colorService';
import { formatBattingAvg, formatERA } from '../../../services/statFormatUtils';
import MobilePlayerLink from '../components/MobilePlayerLink';

export const BOX_HITTING_CONFIG = {
  'AB':  { label: 'AB',  width: '28px', statsField: 'at_bats',      totalsField: 'ab',  fmt: 'int' },
  'R':   { label: 'R',   width: '24px', statsField: 'runs',         totalsField: 'r',   fmt: 'int' },
  'H':   { label: 'H',   width: '24px', statsField: 'hits',         totalsField: 'h',   fmt: 'int' },
  'HR':  { label: 'HR',  width: '28px', statsField: 'home_runs',    totalsField: 'hr',  fmt: 'int' },
  'RBI': { label: 'RBI', width: '30px', statsField: 'rbi',          totalsField: 'rbi', fmt: 'int' },
  'SB':  { label: 'SB',  width: '24px', statsField: 'stolen_bases', totalsField: 'sb',  fmt: 'int' },
  'BB':  { label: 'BB',  width: '24px', statsField: 'walks',        totalsField: 'bb',  fmt: 'int' },
  'K':   { label: 'SO',  width: '24px', statsField: 'strikeouts',   totalsField: 'so',  fmt: 'int' },
  'AVG': { label: 'AVG', width: '36px', statsField: 'batting_avg',  totalsField: 'avg', fmt: 'avg' },
  'OPS': { label: 'OPS', width: '36px', statsField: 'ops',          totalsField: 'ops', fmt: 'avg' },
  'OBP': { label: 'OBP', width: '36px', statsField: 'obp',          totalsField: 'obp', fmt: 'avg' },
  'SLG': { label: 'SLG', width: '36px', statsField: 'slg',          totalsField: 'slg', fmt: 'avg' },
};

export const BOX_PITCHING_CONFIG = {
  'W':    { label: 'W',    width: '22px', statsField: 'wins',             totalsField: 'w',    fmt: 'int' },
  'L':    { label: 'L',    width: '22px', statsField: 'losses',           totalsField: 'l',    fmt: 'int' },
  'SV':   { label: 'SV',   width: '24px', statsField: 'saves',            totalsField: 'sv',   fmt: 'int' },
  'IP':   { label: 'IP',   width: '28px', statsField: 'innings_pitched',  totalsField: 'ip',   fmt: 'ip' },
  'H':    { label: 'H',    width: '22px', statsField: 'hits_allowed',     totalsField: 'h',    fmt: 'int' },
  'ER':   { label: 'ER',   width: '24px', statsField: 'earned_runs',      totalsField: 'er',   fmt: 'int' },
  'BB':   { label: 'BB',   width: '24px', statsField: 'walks_allowed',    totalsField: 'bb',   fmt: 'int' },
  'SO':   { label: 'SO',   width: '24px', statsField: 'strikeouts',       totalsField: 'so',   fmt: 'int' },
  'QS':   { label: 'QS',   width: '24px', statsField: 'quality_starts',   totalsField: 'qs',   fmt: 'int' },
  'ERA':  { label: 'ERA',  width: '34px', statsField: 'era',              totalsField: 'era',  fmt: 'era' },
  'WHIP': { label: 'WHIP', width: '36px', statsField: 'whip',             totalsField: 'whip', fmt: 'avg' },
  'HD':   { label: 'HLD',  width: '28px', statsField: 'holds',            totalsField: 'hd',   fmt: 'int' },
  'HLD':  { label: 'HLD',  width: '28px', statsField: 'holds',            totalsField: 'hld',  fmt: 'int' },
  'BS':   { label: 'BS',   width: '24px', statsField: 'blown_saves',      totalsField: 'bs',   fmt: 'int' },
};

export const BOX_HITTING_CONTEXT = ['AB'];
export const BOX_PITCHING_CONTEXT = ['IP', 'H', 'ER'];

/**
 * Build the ordered column list: fixed context columns first, then each
 * scored category (deduped) that has a config entry. `scoringAbbrs` comes
 * from league_settings.scoring_categories.{hitting|pitching}.
 */
export const buildBoxColumns = (scoringAbbrs, contextAbbrs, configMap) => {
  const seen = new Set();
  const result = [];
  for (const abbr of [...contextAbbrs, ...(scoringAbbrs || [])]) {
    if (seen.has(abbr) || !configMap[abbr]) continue;
    seen.add(abbr);
    result.push({ abbr, ...configMap[abbr] });
  }
  return result;
};

/** Format a single box-score cell value per its column fmt. */
export const formatBoxCell = (value, fmt) => {
  if (value === null || value === undefined) {
    if (fmt === 'avg') return '.000';
    if (fmt === 'era') return '0.00';
    if (fmt === 'ip') return '0.0';
    return 0;
  }
  if (fmt === 'avg') return formatBattingAvg(value);
  if (fmt === 'era') return formatERA(value);
  if (fmt === 'ip') return (parseFloat(value) || 0).toFixed(1);
  return value;
};

const displayLineupPosition = (player) => {
  const lp = player?.lineup_position || '-';
  return lp.includes('_') ? lp.split('_')[0] : lp;
};

/**
 * Adapt the box-score config to MobileDynastyTable columns (prop A sticky
 * context columns). Renders use the desktop signature render(player, rowType,
 * item): a real player row in the body, and (null, 'accrued', totalsRow) in
 * the totals row — so one column set drives both. `totalsRow` is the
 * display-ready object keyed by each column's totalsField.
 *
 * Consumed by MobileBoxScoreTile, MobileTeamHome, and MobileTeamBoxScore so
 * the frozen POS + Player (+ Tm) and every scored column come from ONE source.
 */
export const buildMobileBoxScoreColumns = ({
  scoringCategories,
  activeTab,
  onPlayerClick,
  includeTeamColumn = false,
}) => {
  const isHitting = activeTab === 'hitting';
  const scoring = scoringCategories || {};
  const boxCols = isHitting
    ? buildBoxColumns(scoring.hitting, BOX_HITTING_CONTEXT, BOX_HITTING_CONFIG)
    : buildBoxColumns(scoring.pitching, BOX_PITCHING_CONTEXT, BOX_PITCHING_CONFIG);

  const columns = [];

  // POS (frozen) — yellow lineup position in the body; "-" in the totals row.
  columns.push({
    key: 'position_slot',
    title: 'POS',
    width: '22px',
    sticky: true,
    textAlign: 'center',
    render: (player) =>
      player
        ? <span style={{ color: dynastyTheme.tokens.colors.warning, fontWeight: 700, fontSize: '9px' }}>{displayLineupPosition(player)}</span>
        : <span style={{ color: dynastyTheme.tokens.colors.neutral[300], fontWeight: 700 }}>-</span>,
  });

  // Player (frozen) — canonical MobilePlayerLink in the body (hyperlinked,
  // opens the player modal); "TEAM TOTALS" in the totals row.
  columns.push({
    key: 'player_name',
    title: 'Player',
    width: '100px',
    sticky: true,
    textAlign: 'left',
    render: (player) => {
      if (!player) {
        return <span style={{ color: dynastyTheme.tokens.colors.neutral[300], fontWeight: 700, fontSize: '10px' }}>TEAM TOTALS</span>;
      }
      return (
        <MobilePlayerLink
          mlbId={player.player_id}
          name={player.player_name}
          onClick={onPlayerClick}
          style={{ fontSize: 11, fontWeight: 500, width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        />
      );
    },
  });

  // Tm (full box-score page only) — a regular scrolling column (NOT frozen,
  // matching the existing box-score page). "-" in the totals row.
  if (includeTeamColumn) {
    columns.push({
      key: 'mlb_team',
      title: 'Tm',
      width: '26px',
      textAlign: 'center',
      render: (player) =>
        player
          ? <span style={{ color: dynastyTheme.tokens.colors.neutral[400], fontSize: '10px' }}>{player.info?.mlb_team || player.mlb_team || '-'}</span>
          : '-',
    });
  }

  // Scored + context stat columns.
  for (const col of boxCols) {
    columns.push({
      key: col.abbr,
      title: col.label,
      width: col.width,
      textAlign: 'center',
      render: (player, rowType, item) => {
        if (!player) {
          // Totals row: `item` is the display-ready totals object.
          return item?.[col.totalsField] ?? '-';
        }
        const stats = isHitting ? (player.stats?.hitting || {}) : (player.stats?.pitching || {});
        return formatBoxCell(stats[col.statsField], col.fmt);
      },
    });
  }

  return columns;
};
